import React, { useState, useEffect, useRef } from 'react';
import { X, MapPin, Search, Save, RotateCcw, Building2 } from 'lucide-react';

const ProductionLocationModal = ({
  isOpen,
  onClose,
  onSave,
  googleMapsLoaded,
  initialLocation = null,
  productionName = null,
  subjectLabel = 'Lieferwerk Standort',
  searchHintPrefix = 'Asphaltmischwerk',
}) => {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const [marker, setMarker] = useState(null);

  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [searchAddress, setSearchAddress] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpen && googleMapsLoaded && mapRef.current && !map) {
      try {
        let initialCenter = { lat: 51.1657, lng: 10.4515 };
        let initialZoom = 6;

        if (initialLocation?.coordinates) {
          initialCenter = initialLocation.coordinates;
          initialZoom = 13;
        }

        const mapInstance = new window.google.maps.Map(mapRef.current, {
          zoom: initialZoom,
          center: initialCenter,
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        });

        const geocoderInstance = new window.google.maps.Geocoder();

        mapInstance.addListener('click', (event) => {
          placeMarker(event.latLng, mapInstance, geocoderInstance);
        });

        setMap(mapInstance);
        setGeocoder(geocoderInstance);

        if (initialLocation?.coordinates) {
          placeMarker(initialLocation.coordinates, mapInstance, geocoderInstance, false);
        }
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
      }
    }
  }, [isOpen, googleMapsLoaded, map, initialLocation]);

  const toLatNumber = (latLng) => typeof latLng?.lat === 'function' ? latLng.lat() : latLng?.lat;
  const toLngNumber = (latLng) => typeof latLng?.lng === 'function' ? latLng.lng() : latLng?.lng;

  const placeMarker = (latLng, mapInstance = map, geocoderInstance = geocoder, shouldGeocode = true) => {
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    if (marker) {
      marker.setMap(null);
    }

    const newMarker = new window.google.maps.Marker({
      position: latLng,
      map: mapInstance,
      title: productionName || 'Lieferwerk',
      draggable: true,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="12" fill="#0d9488" stroke="white" stroke-width="3"/>
            <text x="16" y="21" text-anchor="middle" fill="white" font-family="Arial" font-size="12" font-weight="bold">🏭</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 16),
      },
    });

    newMarker.addListener('dragend', (event) => {
      placeMarker(event.latLng, mapInstance, geocoderInstance);
    });

    markerRef.current = newMarker;
    setMarker(newMarker);

    if (shouldGeocode && geocoderInstance) {
      geocoderInstance.geocode({ location: latLng }, (results, status) => {
        if (status === 'OK' && results[0]) {
          setSelectedLocation({
            coordinates: latLng,
            address: results[0].formatted_address,
            lat: toLatNumber(latLng),
            lng: toLngNumber(latLng)
          });
          setHasChanges(true);
        }
      });
    } else if (!shouldGeocode) {
      setSelectedLocation(prev => ({
        ...prev,
        coordinates: latLng,
        lat: toLatNumber(latLng),
        lng: toLngNumber(latLng)
      }));
    }
  };

  const handleAddressSearch = async (e) => {
    e.preventDefault();
    if (!geocoder || !searchAddress.trim()) return;

    setIsSearching(true);
    setSearchError(null);

    try {
      geocoder.geocode({ address: searchAddress.trim(), region: 'de' }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          const address = results[0].formatted_address;

          map.setCenter(location);
          map.setZoom(15);

          placeMarker(location, map, geocoder, false);

          setSelectedLocation({
            coordinates: location,
            address: address,
            lat: location.lat(),
            lng: location.lng()
          });
          setHasChanges(true);
        } else {
          setSearchError('Adresse nicht gefunden. Bitte versuchen Sie einen anderen Suchbegriff.');
        }
        setIsSearching(false);
      });
    } catch (error) {
      console.error('Geocoding error:', error);
      setSearchError('Adresssuche fehlgeschlagen');
      setIsSearching(false);
    }
  };

  const handleReset = () => {
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    if (marker) {
      marker.setMap(null);
      setMarker(null);
    }
    setSelectedLocation(initialLocation);
    setSearchAddress('');
    setSearchError(null);
    setHasChanges(false);

    if (initialLocation?.coordinates && map) {
      map.setCenter(initialLocation.coordinates);
      placeMarker(initialLocation.coordinates, map, geocoder, false);
    }
  };

  const handleSave = () => {
    if (selectedLocation && onSave) {
      onSave(selectedLocation);
      setHasChanges(false);
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedLocation(initialLocation);
      setSearchAddress('');
      setSearchError(null);
      setHasChanges(false);
    }
  }, [isOpen, initialLocation]);

  useEffect(() => {
    if (!isOpen) {
      setMap(null);
      setGeocoder(null);
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      if (marker) {
        marker.setMap(null);
        setMarker(null);
      }
    }
  }, [isOpen, marker]);

  if (!isOpen) return null;

  const presetSearch = productionName
    ? (searchHintPrefix ? `${searchHintPrefix} ${productionName}` : productionName)
    : '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center flex-1">
            <Building2 className="h-6 w-6 text-teal-600 mr-3" />
            <div className="flex-1">
              <h2 className="text-xl font-semibold">
                {subjectLabel}{productionName ? `: ${productionName}` : ''}
              </h2>
              <div className="text-sm text-gray-600 mt-1">
                {selectedLocation ? (
                  <div>
                    <div className="truncate max-w-md">{selectedLocation.address}</div>
                    <div className="text-xs text-gray-500">
                      {selectedLocation.lat?.toFixed(6)}, {selectedLocation.lng?.toFixed(6)}
                    </div>
                  </div>
                ) : (
                  <span>Klicken Sie auf die Karte oder suchen Sie nach der korrekten Adresse.</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mr-4">
            {hasChanges && (
              <>
                <button
                  onClick={handleReset}
                  className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors flex items-center gap-1"
                >
                  <RotateCcw className="h-3 w-3" />
                  Zurücksetzen
                </button>
                <button
                  onClick={handleSave}
                  className="px-3 py-1 text-xs bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors flex items-center gap-1"
                >
                  <Save className="h-3 w-3" />
                  Speichern
                </button>
              </>
            )}
          </div>

          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <form onSubmit={handleAddressSearch} className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchAddress}
                  onChange={(e) => setSearchAddress(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder={presetSearch ? `z.B. "${presetSearch}"` : 'Lieferwerk-Adresse suchen...'}
                  disabled={isSearching}
                />
              </div>
              <button
                type="submit"
                disabled={isSearching || !searchAddress.trim()}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSearching ? 'Suche...' : 'Suchen'}
              </button>
            </form>

            {searchError && (
              <div className="mt-2 text-sm text-red-600">{searchError}</div>
            )}
          </div>

          <div className="flex-1 relative">
            <div
              ref={mapRef}
              className="w-full h-full min-h-[400px] cursor-crosshair"
              style={{ minHeight: '400px' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionLocationModal;
